'use client';

import React, { useMemo } from 'react';
import { tv } from 'tailwind-variants';
import type { FormValues, FormInstance, AddMoreConfig } from '../../types';
import { loadFormState } from '../../utils/storage';
import { useUIText } from '../../hooks/useUIText';

interface EstateProgressBarProps {
  values: FormValues;
  completedForms: Record<string, FormInstance[]>;
  addMoreConfig: AddMoreConfig;
  currentFormValues?: FormValues; // Current form values being entered (not yet saved)
  localStorageKey?: string; // Optional localStorage key to load estate values from
  editingInstanceId?: string | null; // Instance ID being edited (to exclude from completedForms calculation)
}

/**
 * Reusable component to display estate or cash progress bar showing remaining amount
 * Only used in AddMore forms
 */
export const EstateProgressBar: React.FC<EstateProgressBarProps> = ({
  values,
  completedForms,
  addMoreConfig,
  currentFormValues = {},
  localStorageKey,
  editingInstanceId,
}) => {
  const { getProgressText } = useUIText();
  const progressBarType = addMoreConfig.progressBarType || 'estate';
  const showProgressBar = addMoreConfig.showProgressBar === true;
  // Radio inline-cards pass an empty groupKey; AddMore steps pass the real group key.
  // Without this, current typing is never included and the bar appears frozen.
  const useUnscopedCurrentFormValues = !addMoreConfig.groupKey;

  // Helper function to parse currency values (handles £, commas, spaces)
  const parseCurrency = (value: unknown): number => {
    if (value === undefined || value === null || value === '') return 0;
    const cleaned = String(value).replace(/[£,\s]/g, '');
    return parseFloat(cleaned) || 0;
  };

  // Merge estate values from multiple sources (aligned with validation mergeEstateValues):
  // 1. Start from current values
  // 2. localStorage fills gaps for estate totals
  // 3. completedForms['estate-estimation-group'] overwrites when set — source of truth
  const mergedValues = useMemo(() => {
    const merged = { ...values };

    // Priority 1: Check localStorage as fallback for gaps (before estate instance overlay)
    if (localStorageKey) {
      try {
        const stored = loadFormState(localStorageKey);
        if (stored?.values) {
          // Merge estimatedPropertyValue and estimatedCashEstateValue from localStorage if not already set
          if (
            (merged.estimatedPropertyValue === undefined ||
              merged.estimatedPropertyValue === null ||
              merged.estimatedPropertyValue === '') &&
            stored.values.estimatedPropertyValue !== undefined &&
            stored.values.estimatedPropertyValue !== null &&
            stored.values.estimatedPropertyValue !== ''
          ) {
            merged.estimatedPropertyValue = stored.values.estimatedPropertyValue;
          }
          if (
            (merged.estimatedCashEstateValue === undefined ||
              merged.estimatedCashEstateValue === null ||
              merged.estimatedCashEstateValue === '') &&
            stored.values.estimatedCashEstateValue !== undefined &&
            stored.values.estimatedCashEstateValue !== null &&
            stored.values.estimatedCashEstateValue !== ''
          ) {
            merged.estimatedCashEstateValue = stored.values.estimatedCashEstateValue;
          }
        }
      } catch (_e) {
        // Silently fail - values will just use what's in the current values object
      }
    }

    // Estate-estimation step wins over values + localStorage (matches validation mergeEstateValues)
    const estateEstimationInstances = completedForms['estate-estimation-group'] || [];
    if (estateEstimationInstances.length > 0) {
      const estateData = estateEstimationInstances[0].data;
      if (
        estateData.estimatedPropertyValue !== undefined &&
        estateData.estimatedPropertyValue !== null &&
        estateData.estimatedPropertyValue !== ''
      ) {
        merged.estimatedPropertyValue = estateData.estimatedPropertyValue;
      }
      if (
        estateData.estimatedCashEstateValue !== undefined &&
        estateData.estimatedCashEstateValue !== null &&
        estateData.estimatedCashEstateValue !== ''
      ) {
        merged.estimatedCashEstateValue = estateData.estimatedCashEstateValue;
      }
    }

    return merged;
  }, [values, localStorageKey, completedForms]);
  const { totalAmount, remainingAmount, allocatedPercentage } = useMemo(() => {
    if (progressBarType === 'estate') {
      // Estate calculation: property value
      const propertyValue = parseCurrency(mergedValues.estimatedPropertyValue);
      const totalAmount = propertyValue;

      if (totalAmount === 0) {
        return {
          totalAmount: 0,
          allocatedAmount: 0,
          remainingAmount: 0,
          allocatedPercentage: 0,
        };
      }

      // Calculate allocated from charities (percentage-based)
      // Get from completedForms['charities-group'] OR currentFormValues if on charities step
      const charityInstances = (completedForms['charities-group'] || []).filter(
        (instance) => instance.instanceId !== editingInstanceId
      );
      let charityAllocatedAmount = 0;
      charityInstances.forEach((instance) => {
        const percentage = parseFloat(String(instance.data.charityPercentage || 0)) || 0;
        charityAllocatedAmount += (totalAmount * percentage) / 100;
      });

      // Include current form value on charities step or radio inline-cards (empty groupKey)
      if (useUnscopedCurrentFormValues || addMoreConfig.groupKey === 'charities-group') {
        const currentPercentage = parseFloat(String(currentFormValues.charityPercentage || 0)) || 0;
        if (currentPercentage > 0) {
          charityAllocatedAmount += (totalAmount * currentPercentage) / 100;
        }
      }

      // Calculate allocated from children amounts (percentage-based on estate)
      const childrenInstances = (completedForms['children-group'] || []).filter(
        (instance) => instance.instanceId !== editingInstanceId
      );
      let childrenAllocatedPercent = 0;
      childrenInstances.forEach((instance) => {
        const pct = parseFloat(String(instance.data.childAmount || 0)) || 0;
        childrenAllocatedPercent += pct;
      });
      if (useUnscopedCurrentFormValues || addMoreConfig.groupKey === 'children-group') {
        const currentPct = parseFloat(String(currentFormValues.childAmount || 0)) || 0;
        if (currentPct > 0) {
          childrenAllocatedPercent += currentPct;
        }
      }

      // Calculate allocated from spouse partner children amounts
      const spousePartnerChildrenInstances = (
        completedForms['spousePartnerChildrenFromPreviousRelationship-group'] || []
      ).filter((instance) => instance.instanceId !== editingInstanceId);
      spousePartnerChildrenInstances.forEach((instance) => {
        const pct = parseFloat(String(instance.data.spousePartnerChildAmount || 0)) || 0;
        childrenAllocatedPercent += pct;
      });
      if (
        useUnscopedCurrentFormValues ||
        addMoreConfig.groupKey === 'spousePartnerChildrenFromPreviousRelationship-group'
      ) {
        const currentPct = parseFloat(String(currentFormValues.spousePartnerChildAmount || 0)) || 0;
        if (currentPct > 0) {
          childrenAllocatedPercent += currentPct;
        }
      }
      childrenAllocatedPercent = Math.min(100, Math.max(0, childrenAllocatedPercent));
      const childrenAllocatedAmount = (totalAmount * childrenAllocatedPercent) / 100;

      const allocatedAmount = charityAllocatedAmount + childrenAllocatedAmount;
      const remainingAmount = Math.max(0, totalAmount - allocatedAmount);
      const allocatedPercentage =
        totalAmount > 0 ? Math.round((allocatedAmount / totalAmount) * 100) : 0;

      return {
        totalAmount,
        allocatedAmount,
        remainingAmount,
        allocatedPercentage: allocatedPercentage > 100 ? 100 : allocatedPercentage,
      };
    } else if (progressBarType === 'cash') {
      // Cash calculation: cash estate value only
      const cashEstateValue = parseCurrency(mergedValues.estimatedCashEstateValue);
      const totalAmount = cashEstateValue;

      if (totalAmount === 0) {
        return {
          totalAmount: 0,
          allocatedAmount: 0,
          remainingAmount: 0,
          allocatedPercentage: 0,
        };
      }

      // Calculate allocated from cash gifts to charity
      // EXCLUDE the instance being edited to avoid double-counting
      const cashGiftInstances = (completedForms['cash-gift-group'] || []).filter(
        (instance) => instance.instanceId !== editingInstanceId
      );
      let cashGiftAllocatedAmount = 0;
      cashGiftInstances.forEach((instance) => {
        const amount =
          parseFloat(String(instance.data.cashGiftAmount || 0).replace(/[£,\s]/g, '')) || 0;
        cashGiftAllocatedAmount += amount;
      });

      // Calculate allocated from cash gifts to someone
      // EXCLUDE the instance being edited to avoid double-counting
      const cashGiftToSomeoneInstances = (
        completedForms['cash-gift-to-someone-group'] || []
      ).filter((instance) => instance.instanceId !== editingInstanceId);
      let cashGiftToSomeoneAllocatedAmount = 0;
      cashGiftToSomeoneInstances.forEach((instance) => {
        const amount =
          parseFloat(String(instance.data.cashGiftToSomeoneAmount || 0).replace(/[£,\s]/g, '')) ||
          0;
        cashGiftToSomeoneAllocatedAmount += amount;
      });

      // Include current form values if they're being entered
      if (useUnscopedCurrentFormValues || addMoreConfig.groupKey === 'cash-gift-group') {
        const currentAmount =
          parseFloat(String(currentFormValues.cashGiftAmount || 0).replace(/[£,\s]/g, '')) || 0;
        if (currentAmount > 0) {
          cashGiftAllocatedAmount += currentAmount;
        }
      }

      if (useUnscopedCurrentFormValues || addMoreConfig.groupKey === 'cash-gift-to-someone-group') {
        const currentAmount =
          parseFloat(
            String(currentFormValues.cashGiftToSomeoneAmount || 0).replace(/[£,\s]/g, '')
          ) || 0;
        if (currentAmount > 0) {
          cashGiftToSomeoneAllocatedAmount += currentAmount;
        }
      }

      const allocatedAmount = cashGiftAllocatedAmount + cashGiftToSomeoneAllocatedAmount;
      const remainingAmount = Math.max(0, totalAmount - allocatedAmount);
      const allocatedPercentage =
        totalAmount > 0 ? Math.round((allocatedAmount / totalAmount) * 100) : 0;

      return {
        totalAmount,
        allocatedAmount,
        remainingAmount,
        allocatedPercentage: allocatedPercentage > 100 ? 100 : allocatedPercentage,
      };
    } else if (progressBarType === 'combined') {
      // Combined calculation: Estate + Cash together
      const propertyValue = parseCurrency(mergedValues.estimatedPropertyValue);
      const cashEstateValue = parseCurrency(mergedValues.estimatedCashEstateValue);
      const totalAmount = propertyValue + cashEstateValue;

      if (totalAmount === 0) {
        return {
          totalAmount: 0,
          allocatedAmount: 0,
          remainingAmount: 0,
          allocatedPercentage: 0,
        };
      }

      // Calculate allocated from charities (percentage-based on estate)
      // Get from completedForms['charities-group'] OR currentFormValues if on charities step
      const charityInstances = (completedForms['charities-group'] || []).filter(
        (instance) => instance.instanceId !== editingInstanceId
      );
      let charityAllocatedAmount = 0;
      charityInstances.forEach((instance) => {
        const percentage = parseFloat(String(instance.data.charityPercentage || 0)) || 0;
        charityAllocatedAmount += (propertyValue * percentage) / 100;
      });

      // Include current form value if user is on charities step
      if (useUnscopedCurrentFormValues || addMoreConfig.groupKey === 'charities-group') {
        const currentPercentage = parseFloat(String(currentFormValues.charityPercentage || 0)) || 0;
        if (currentPercentage > 0) {
          charityAllocatedAmount += (propertyValue * currentPercentage) / 100;
        }
      }

      // Calculate allocated from cash gifts to charity
      // Get from completedForms['cash-gift-group'] OR currentFormValues if on cash-gift step
      const cashGiftInstances = (completedForms['cash-gift-group'] || []).filter(
        (instance) => instance.instanceId !== editingInstanceId
      );
      let cashGiftAllocatedAmount = 0;
      cashGiftInstances.forEach((instance) => {
        const amount = parseCurrency(instance.data.cashGiftAmount);
        cashGiftAllocatedAmount += amount;
      });

      // Include current form value if user is on cash-gift step
      if (useUnscopedCurrentFormValues || addMoreConfig.groupKey === 'cash-gift-group') {
        const currentAmount = parseCurrency(currentFormValues.cashGiftAmount);
        if (currentAmount > 0) {
          cashGiftAllocatedAmount += currentAmount;
        }
      }

      // Calculate allocated from cash gifts to someone
      // Get from completedForms['cash-gift-to-someone-group'] OR currentFormValues if on cash-gift-to-someone step
      const cashGiftToSomeoneInstances = (
        completedForms['cash-gift-to-someone-group'] || []
      ).filter((instance) => instance.instanceId !== editingInstanceId);
      let cashGiftToSomeoneAllocatedAmount = 0;
      cashGiftToSomeoneInstances.forEach((instance) => {
        const amount = parseCurrency(instance.data.cashGiftToSomeoneAmount);
        cashGiftToSomeoneAllocatedAmount += amount;
      });

      // Include current form value if user is on cash-gift-to-someone step
      if (useUnscopedCurrentFormValues || addMoreConfig.groupKey === 'cash-gift-to-someone-group') {
        const currentAmount = parseCurrency(currentFormValues.cashGiftToSomeoneAmount);
        if (currentAmount > 0) {
          cashGiftToSomeoneAllocatedAmount += currentAmount;
        }
      }

      // Calculate allocated from children amounts
      // Get from completedForms['children-group'] OR currentFormValues if on children step
      const childrenInstances = (completedForms['children-group'] || []).filter(
        (instance) => instance.instanceId !== editingInstanceId
      );

      let childrenAllocatedPercent = 0;

      childrenInstances.forEach((instance) => {
        const pct = parseFloat(String(instance.data.childAmount || 0)) || 0;
        childrenAllocatedPercent += pct;
      });

      // Include current form value if user is on children step
      if (useUnscopedCurrentFormValues || addMoreConfig.groupKey === 'children-group') {
        const currentPct = parseFloat(String(currentFormValues.childAmount || 0)) || 0;
        if (currentPct > 0) {
          childrenAllocatedPercent += currentPct;
        }
      }

      // Calculate allocated from spouse partner children amounts
      // Get from completedForms['spousePartnerChildrenFromPreviousRelationship-group'] OR currentFormValues if on spouse partner children step
      const spousePartnerChildrenInstances = (
        completedForms['spousePartnerChildrenFromPreviousRelationship-group'] || []
      ).filter((instance) => instance.instanceId !== editingInstanceId);

      spousePartnerChildrenInstances.forEach((instance) => {
        const pct = parseFloat(String(instance.data.spousePartnerChildAmount || 0)) || 0;
        childrenAllocatedPercent += pct;
      });

      // Include current form value if user is on spouse partner children step
      if (
        useUnscopedCurrentFormValues ||
        addMoreConfig.groupKey === 'spousePartnerChildrenFromPreviousRelationship-group'
      ) {
        const currentPct = parseFloat(String(currentFormValues.spousePartnerChildAmount || 0)) || 0;
        if (currentPct > 0) {
          childrenAllocatedPercent += currentPct;
        }
      }

      // Clamp percentage to [0, 100] (optional but recommended)
      childrenAllocatedPercent = Math.min(100, Math.max(0, childrenAllocatedPercent));

      // In combined mode, apply children % to totalAmount (property + cash)
      // If you want children % only on estate, change base = propertyValue
      const childrenAllocatedAmount = (totalAmount * childrenAllocatedPercent) / 100;

      // Combined allocated amount = charity allocations (from estate) + cash gifts + children amounts
      const allocatedAmount =
        charityAllocatedAmount +
        cashGiftAllocatedAmount +
        cashGiftToSomeoneAllocatedAmount +
        childrenAllocatedAmount;
      const remainingAmount = totalAmount - allocatedAmount;
      const allocatedPercentage = Math.round(
        totalAmount > 0 ? (allocatedAmount / totalAmount) * 100 : 0
      );

      return {
        totalAmount,
        allocatedAmount,
        remainingAmount: Math.max(0, remainingAmount),
        allocatedPercentage: allocatedPercentage > 100 ? 100 : allocatedPercentage,
      };
    }

    // Default fallback
    return {
      totalAmount: 0,
      allocatedAmount: 0,
      remainingAmount: 0,
      allocatedPercentage: 0,
    };
  }, [
    mergedValues,
    completedForms,
    addMoreConfig,
    currentFormValues,
    progressBarType,
    editingInstanceId,
    useUnscopedCurrentFormValues,
  ]);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Only hide if showProgressBar is false
  if (!showProgressBar) {
    return null;
  }

  // If totalAmount is 0, show 100% remaining (user might not have entered estate values yet)
  const progressPercentage = totalAmount > 0 ? Math.max(0, 100 - allocatedPercentage) : 100;
  const displayRemaining = totalAmount > 0 ? remainingAmount : 0;

  const { container, remainingText, progressBarContainer, progressBarFill } = TAILWIND_VARIANTS();

  return (
    <div className={container()}>
      <span className={remainingText()}>
        {progressBarType === 'estate' || progressBarType === 'combined'
          ? progressPercentage + '%'
          : formatCurrency(displayRemaining)}{' '}
        {getProgressText('remaining', 'remaining')}
      </span>

      {/* Progress bar */}

      <div className={progressBarContainer()}>
        <div
          className={progressBarFill()}
          style={{ width: `${progressPercentage > 0 ? progressPercentage : 0}%` }}
        />
      </div>
    </div>
  );
};

const TAILWIND_VARIANTS = tv({
  slots: {
    container: ['w-full', 'max-w-[410px]', 'flex', 'flex-col', 'gap-spacing-spacing-8'],
    remainingText: [
      'font-typography-font-family-body',
      'text-typography-font-size-body-large',
      'leading-typography-line-height-body-large',
      'font-normal',
      'text-color-core-color-1-Navy',
    ],
    progressBarContainer: [
      'w-full',
      'h-3.5',
      'bg-color-grayscale-100',
      'rounded-general-border-radius-container',
      'overflow-hidden',
      'relative',
    ],
    progressBarFill: [
      'h-full',
      'bg-color-core-color-4-SkyBlue',
      'rounded-general-border-radius-container',
      'transition-all',
      'duration-300',
      'ease-in-out',
    ],
  },
});
